// Remembers where a signed-out user was headed, so sign-in can put them back
// instead of dropping them on the workspace chooser.
//
// The full in-app path is stored verbatim, including any /v2 prefix — that
// prefix is what says which app the link belongs to, so keeping it means
// nothing has to be taken apart and rebuilt later.
const KEY = 'auth_return_to';

// troopr-ui (the v2 app) is built with base '/v2/', so its router strips that
// prefix before handing a path over — "/v2/projects" reaches us as "/projects".
// It stashes the path in sessionStorage under this same name; reading that is
// how we know the path came from v2 and needs its prefix put back.

// Auth screens: returning to one after signing in would bounce the user out
// again.
const BLOCKED = [
  '/troopr/access',
  '/troopr/legacy_access',
  '/troopr/slack_callback',
  '/signup',
  '/verification',
  '/user-workspaces',
  '/slack_login',
  '/slack_helpdesk_login',
];

// Only same-origin, in-app paths survive. Anything absolute, protocol-relative
// or backslash-smuggled is dropped, so a crafted returnTo cannot turn sign-in
// into an open redirect to another site.
export function sanitizeReturnPath(path) {
  if (!path || typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//') || trimmed.startsWith('/\\')) return null;
  try {
    const base = 'http://localhost';
    const parsed = new URL(trimmed, base);
    if (parsed.origin !== base) return null;
    const clean = parsed.pathname + parsed.search + parsed.hash;
    const lower = clean.toLowerCase().replace(/^\/v2/, '') || '/';
    if (lower === '/') return null;
    // Match on a segment boundary so a real route that merely starts with a
    // blocked name (/signups) is kept.
    const blocked = BLOCKED.some(
      (b) => lower === b || lower.startsWith(b + '/') || lower.startsWith(b + '?')
    );
    return blocked ? null : clean;
  } catch (e) {
    return null;
  }
}

// localStorage, so the path survives the full-page hop out to Google/Slack and
// back. Wrapped because storage throws in some privacy modes — a lost return
// path is a small downgrade, never a broken sign-in.
export function saveReturnPath(path) {
  const clean = sanitizeReturnPath(path);
  if (!clean) return;
  try {
    localStorage.setItem(KEY, clean);
  } catch (e) {
    /* ignore */
  }
}

// Reads the pending path: the ?returnTo= param first (how the backend hands it
// back after an OAuth hop), then the stored one. Re-sanitised on the way out,
// since the query string is user-supplied.
export function readReturnPath(search) {
  let fromQuery = null;
  try {
    fromQuery = new URLSearchParams(search || '').get('returnTo');
  } catch (e) {
    fromQuery = null;
  }
  let stored = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch (e) {
    stored = null;
  }
  // Written by troopr-ui's route guard when the deep link was a v2 one.
  let fromV2 = null;
  try {
    fromV2 = sessionStorage.getItem(KEY);
  } catch (e) {
    fromV2 = null;
  }
  // troopr-ui wrote this, so the /v2 prefix was stripped on the way out —
  // restore it or the user lands on the v1 route of the same name. Validate
  // BEFORE prefixing: "/v2" glued onto "https://evil.com" or "//evil.com" would
  // otherwise turn a rejected value into one that passes.
  if (fromV2) {
    const safe = sanitizeReturnPath(fromV2);
    if (safe) return addV2Base(safe);
  }
  return sanitizeReturnPath(fromQuery) || sanitizeReturnPath(stored);
}

// "/projects" -> "/v2/projects" (no-op when the prefix is already there).
function addV2Base(path) {
  if (!path || typeof path !== 'string') return path;
  if (path === '/v2' || path.startsWith('/v2/')) return path;
  return '/v2' + path;
}

export function clearReturnPath() {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
  // troopr-ui's copy, or a stale v2 path would outrank the next real one.
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
}

// The stored path is workspace-scoped when it already carries a workspace id;
// otherwise it needs one, and the chooser supplies it after the user picks.
export function isWorkspaceScoped(path) {
  return /^(\/v2)?\/[a-f0-9]{24}(\/|$)/i.test(path || '');
}

// A path belongs to the new UI when it is mounted under /v2.
export function isV2Path(path) {
  return path === '/v2' || (path || '').startsWith('/v2/');
}

// Slot a workspace id into an unscoped path: /v2/projects -> /v2/<wId>/projects
export function withWorkspace(path, workspaceId) {
  if (!path || !workspaceId || isWorkspaceScoped(path)) return path;
  return isV2Path(path)
    ? `/v2/${workspaceId}${path.slice(3)}`
    : `/${workspaceId}${path}`;
}

// A workspace id is a 24-char hex ObjectId. Anything else (a stale or stray
// localStorage value such as "projects") must never be spliced into a URL.
export function isValidWorkspaceId(id) {
  return typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);
}

// A workspace can open a path only when it runs the UI the path belongs to —
// the two apps do not share routes, so a v2 path on a v1 workspace would 404.
export function isCompatibleWorkspace(path, workspace) {
  return (
    !!path &&
    !!workspace &&
    isValidWorkspaceId(workspace._id) &&
    isV2Path(path) === !!workspace.isNewUi
  );
}

// Where a finished sign-in should land for this pending path, as
// { path, workspaceId }, or null when only the chooser can decide.
//
// `workspaces` is the user's list, or absent when it could not be fetched. The
// remembered workspace wins if the list shows it can open the path, else the
// one and only workspace that can. Several candidates (or none) need the user
// to pick — and so does a missing list: a bare remembered id says nothing about
// which UI it runs, and v2 does not bounce a v1 workspace back out.
export function resolveReturnTarget(path, { rememberedWorkspaceId, workspaces } = {}) {
  const safe = sanitizeReturnPath(path);
  if (!safe) return null;
  if (isWorkspaceScoped(safe)) return { path: safe, workspaceId: null };
  if (!Array.isArray(workspaces)) return null;

  const remembered = isValidWorkspaceId(rememberedWorkspaceId)
    ? rememberedWorkspaceId
    : null;
  const compatible = workspaces.filter((w) => isCompatibleWorkspace(safe, w));
  const pick =
    compatible.find((w) => w._id === remembered) ||
    (compatible.length === 1 ? compatible[0] : null);
  return pick ? { path: withWorkspace(safe, pick._id), workspaceId: pick._id } : null;
}

// Append the pending path to an auth URL, for the hops that leave our origin.
export function withReturnParam(url, path) {
  if (!path) return url;
  const sep = url.indexOf('?') === -1 ? '?' : '&';
  return `${url}${sep}returnTo=${encodeURIComponent(path)}`;
}
